/**
 * @file authService.ts
 * @description Authentication Service with TypeScript - Task 18.7
 *
 * Provides typed methods for authentication operations:
 * - register: Create new user account
 * - forgotPassword: Request password reset
 * - resetPassword: Complete password reset
 * - postAuthActions: Handle post-login tournament joining
 * - logout: Clear all auth state
 * - checkUserDetails: Verify current user
 *
 * Reference: .taskmaster/docs/research/task-18-api-services-typescript-migration.md
 * Section 3.6 (implicit migration)
 */

import Cookies from 'js-cookie';

import apiClient from './apiClient';
import { joinTournament, getTournamentDetails } from './tournamentService';
import { getCookieDomainFromUrl } from '../utils';
import type {
  AuthResponse,
  User,
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from '../types/user';

const domain = getCookieDomainFromUrl(process.env.REACT_APP_LANDING_PAGE);

/**
 * Response type for forgot password
 */
interface ForgotPasswordResponse {
  ok: boolean;
}

/**
 * Response type for reset password
 */
interface ResetPasswordResponse {
  ok: boolean;
  user?: User;
}

/**
 * Error with payment UI state for tournament join errors
 */
interface PaymentError {
  uiState?: string;
  status?: number;
  rawError?: unknown;
}

/**
 * Registers a user with the provided username, email, and password.
 *
 * @param username - The username of the user
 * @param email - The email of the user
 * @param password - The password of the user
 * @param anonymous - Whether to create an anonymous user
 * @returns Auth response with jwt and user data
 */
export const register = async (
  username: string,
  email: string,
  password: string,
  anonymous = false,
): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<AuthResponse>('/auth/local/register', {
      username,
      email,
      password,
      anonymous,
    });

    if (response.status === 200) {
      const { jwt, user } = response.data;

      localStorage.setItem('userId', String(user.id));
      localStorage.setItem('jwt', jwt);
      localStorage.setItem('username', user.username);
      localStorage.setItem('email', user.email);

      // Setting cookies
      Cookies.set('userId', String(user.id), {
        expires: 7,
        secure: true,
        sameSite: 'strict',
        domain,
      });
      Cookies.set('jwt', jwt, {
        expires: 7,
        secure: true,
        sameSite: 'none',
        domain,
      });
      Cookies.set('username', user.username, {
        expires: 7,
        secure: true,
        sameSite: 'strict',
        domain,
      });
      Cookies.set('email', user.email, {
        expires: 7,
        secure: true,
        sameSite: 'strict',
        domain,
      });
    }

    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Initiates the password reset process for the user with the provided email.
 *
 * @param email - The email of the user who wants to reset their password
 * @returns Success response
 */
export const forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
  try {
    const response = await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', {
      email,
    });
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * Resets the user's password using the provided code, new password, and password confirmation.
 *
 * @param code - The code received for password reset
 * @param password - The new password for the user
 * @param passwordConfirmation - The confirmation of the new password
 * @returns Success response with optional user data
 */
export const resetPassword = async (
  code: string,
  password: string,
  passwordConfirmation: string,
): Promise<ResetPasswordResponse> => {
  try {
    const response = await apiClient.post<ResetPasswordResponse>('/auth/reset-password', {
      code,
      password,
      passwordConfirmation,
    });
    return response.data;
  } catch (error) {
    const axiosError = error as { response?: { data?: unknown }; message?: string };
    throw axiosError.response ? axiosError.response.data : axiosError.message;
  }
};

/**
 * A function that performs post-authentication actions.
 * For tournaments with entry fees, this function will NOT auto-join but will preserve
 * the intent so the user can complete payment through the UI (Task 14.4).
 */
export const postAuthActions = async (): Promise<void> => {
  const intendToJoinTournament =
    localStorage.getItem('intendToJoinTournament') ?? Cookies.get('intendToJoinTournament');

  if (intendToJoinTournament) {
    setTimeout(async () => {
      try {
        // PAYMENT CHECK - Fetch tournament details to check if paid (Task 14.4)
        const tournamentDetails = await getTournamentDetails(intendToJoinTournament);
        // TEAM_003: Support both legacy { data: { attributes: { entryFee } } }
        // and new flattened { data: { entryFee } } shapes.
        const rawData: any = tournamentDetails?.data;
        const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
        const entryFee = attributes?.entryFee;

        if (entryFee?.type === 'PAID') {
          // DO NOT auto-join paid tournaments - user must complete payment via UI
          console.log(
            `Post-auth: Tournament ${intendToJoinTournament} requires payment (${entryFee.amount} ${entryFee.currency}). ` +
              `Keeping intent in storage for user to complete via join page.`,
          );
          // Keep intendToJoinTournament in storage so user can be prompted
          return;
        }

        // Free tournament - proceed with auto-join
        await joinTournament(intendToJoinTournament);

        // Successfully joined - clear the intent
        localStorage.removeItem('intendToJoinTournament');
        Cookies.remove('intendToJoinTournament', { path: '/', domain });
      } catch (error) {
        console.error('Error in post-auth tournament join:', error);

        // Handle payment-specific errors (Task 14.3)
        const paymentError = error as PaymentError;
        if (paymentError?.uiState) {
          console.warn(
            `Post-auth: Payment error for tournament ${intendToJoinTournament}:`,
            paymentError.uiState,
          );
          // Keep intent in storage for user to complete via proper flow
          return;
        }

        // For other errors, clear the intent to avoid retry loops
        localStorage.removeItem('intendToJoinTournament');
        Cookies.remove('intendToJoinTournament', { path: '/', domain });
      }
    }, 100);
  }
};

/**
 * Clears all authentication state (localStorage and cookies)
 */
export const logout = (): void => {
  localStorage.clear();
  Cookies.remove('userId', { path: '/', domain });
  Cookies.remove('jwt', { path: '/', domain });
  Cookies.remove('username', { path: '/', domain });
  Cookies.remove('email', { path: '/', domain });
  Cookies.remove('intendToJoinTournament', { path: '/', domain });
};

/**
 * Checks the current user's details.
 *
 * @returns User data with populated tournaments
 */
export const checkUserDetails = async (): Promise<User> => {
  const response = await apiClient.get<User>('/users/me?populate=tournaments');
  return response.data;
};
